import { useCallback, useEffect, useState, type ComponentType } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { employeeOnboardingGateway } from '../index';
import { OnboardingLayout } from '../components/OnboardingLayout';
import { AddressSection } from '../sections/AddressSection';
import { PayInformationSection } from '../sections/PayInformationSection';
import { PayMethodSection } from '../sections/PayMethodSection';
import { PayrollDetailsSection } from '../sections/PayrollDetailsSection';
import { PersonalSection } from '../sections/PersonalSection';
import type { SectionProps } from '../sections/sectionProps';
import { WorkDetailsSection } from '../sections/WorkDetailsSection';
import { WorkLocationSection } from '../sections/WorkLocationSection';
import {
  ONBOARDING_SECTIONS,
  TAXES_PLACEHOLDER_PATH,
  sectionPath,
  type EmployeeSetupResponse,
  type OnboardingSectionSlug,
} from '../types';
import { reachableSectionIndex } from '../progress';

const SECTION_COMPONENTS: Record<OnboardingSectionSlug, ComponentType<SectionProps>> = {
  personal: PersonalSection,
  address: AddressSection,
  'work-details': WorkDetailsSection,
  'work-location': WorkLocationSection,
  'pay-method': PayMethodSection,
  'pay-information': PayInformationSection,
  'payroll-details': PayrollDetailsSection,
};

/** `/employee/setup/:section`. The route guard in App.tsx has already established an authenticated Employee session. */
export function EmployeeSetupPage() {
  const { section } = useParams();
  const navigate = useNavigate();
  const [setup, setSetup] = useState<EmployeeSetupResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    employeeOnboardingGateway
      .getSetup()
      .then((s) => {
        if (!cancelled) setSetup(s);
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(friendlyErrorMessage(err, 'We could not load your profile. Please try again.'));
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  /** Re-reads the saved state after a section saves. A failed re-read keeps the last known state; the save itself already succeeded. */
  const refresh = useCallback(async () => {
    try {
      setSetup(await employeeOnboardingGateway.getSetup());
    } catch {
      // Keep what is on screen; the next load will correct it.
    }
  }, []);

  const index = ONBOARDING_SECTIONS.findIndex((s) => s.slug === section);
  if (index < 0) return <Navigate to={sectionPath('personal')} replace />;

  if (loadError) {
    return (
      <OnboardingLayout activeStep={1} activeSection={ONBOARDING_SECTIONS[index]!.slug} reachableIndex={index}>
        <div className="eo-panel">
          <p className="eo-banner eo-banner--error" role="alert">
            {loadError}
          </p>
          <button
            type="button"
            className="eo-btn eo-btn--primary"
            onClick={() => {
              setLoadError(null);
              setAttempt((a) => a + 1);
            }}
          >
            Try again
          </button>
        </div>
      </OnboardingLayout>
    );
  }

  if (!setup) {
    return (
      <OnboardingLayout activeStep={1} activeSection={ONBOARDING_SECTIONS[index]!.slug} reachableIndex={index}>
        <p className="eo-loading" role="status">
          Loading your profile…
        </p>
      </OnboardingLayout>
    );
  }

  const reachable = reachableSectionIndex(setup);
  if (index > reachable) return <Navigate to={sectionPath(ONBOARDING_SECTIONS[reachable]!.slug)} replace />;

  const def = ONBOARDING_SECTIONS[index]!;
  const Section = SECTION_COMPONENTS[def.slug];
  const nextDef = ONBOARDING_SECTIONS[index + 1];
  const prevDef = ONBOARDING_SECTIONS[index - 1];

  return (
    <OnboardingLayout activeStep={1} activeSection={def.slug} reachableIndex={reachable}>
      <Section
        key={def.slug}
        setup={setup}
        refresh={refresh}
        next={() => navigate(nextDef ? sectionPath(nextDef.slug) : TAXES_PLACEHOLDER_PATH)}
        back={prevDef ? () => navigate(sectionPath(prevDef.slug)) : undefined}
      />
    </OnboardingLayout>
  );
}
